import { Cyl, Lathe, Slab } from '../../render/parts'
import type { AssetContext, AssetDefinition } from '../types'

/**
 * Lounge coffee table, 0.40 high. Two silhouettes share the same declared
 * footprint: a rectangular top on four slim tubes with a lower shelf, and a
 * round top on a pedestal, centred inside the same 1.0 x 0.55 cell.
 *
 * Both carry a small accent tray — the one colour note, deliberately subtle.
 */

const WIDTH = 1
const DEPTH = 0.55
const HEIGHT = 0.4
const TOP = 0.028
const UNDER = HEIGHT - TOP

/** Shallow tray, sunk 2 mm into the top so the two faces never share a plane. */
function AccentTray({ accent }: { accent?: string }) {
  return (
    <group>
      <Slab tone="shellSoft" size={[0.3, 0.2, 0.014]} radius={0.02} position={[0, HEIGHT + 0.005, 0]} />
      <Slab
        tone="accent"
        color={accent}
        size={[0.26, 0.16, 0.008]}
        radius={0.014}
        position={[0, HEIGHT + 0.014, 0]}
      />
    </group>
  )
}

function TableCoffee({ accent, variant = 'rect' }: AssetContext) {
  if (variant === 'round') {
    const r = DEPTH / 2
    const columnBase = 0.038
    const columnTop = 0.36
    return (
      <group>
        <Cyl tone="surface" r={r} h={TOP} segments={44} position={[0, HEIGHT - TOP / 2, 0]} />
        <Cyl tone="metal" r={0.082} h={0.024} segments={24} position={[0, UNDER - 0.006, 0]} />
        <Cyl
          tone="shell"
          r={0.055}
          h={columnTop - columnBase}
          segments={24}
          position={[0, (columnTop + columnBase) / 2, 0]}
        />
        <Lathe
          tone="shell"
          segments={36}
          profileKey="table-coffee-foot"
          profile={[
            [0, 0],
            [0.19, 0],
            [0.19, 0.011],
            [0.165, 0.026],
            [0.07, 0.044],
            [0, 0.044],
          ]}
        />
        <group position={[0.06, 0, 0.02]} rotation={[0, -0.16, 0]}>
          <AccentTray accent={accent} />
        </group>
      </group>
    )
  }

  const legX = WIDTH / 2 - 0.06
  const legZ = DEPTH / 2 - 0.06

  return (
    <group>
      <Slab tone="surface" size={[WIDTH, DEPTH, TOP]} radius={0.024} position={[0, HEIGHT - TOP / 2, 0]} />
      {/* Lower shelf, overlapping the legs by ~2 mm on every side. */}
      <Slab tone="shellSoft" size={[0.84, 0.4, 0.016]} radius={0.018} position={[0, 0.13, 0]} />
      {(
        [
          [-legX, -legZ],
          [legX, -legZ],
          [-legX, legZ],
          [legX, legZ],
        ] as const
      ).map(([x, z]) => (
        <Cyl
          key={`${x},${z}`}
          tone="metalDark"
          r={0.021}
          h={UNDER + 0.008}
          segments={16}
          position={[x, (UNDER + 0.008) / 2, z]}
        />
      ))}
      <group position={[-0.28, 0, 0.01]} rotation={[0, 0.12, 0]}>
        <AccentTray accent={accent} />
      </group>
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'table-coffee',
  name: 'Mesa de centro',
  category: 'tables',
  footprint: [WIDTH, DEPTH],
  height: 0.42,
  surfaceHeight: HEIGHT,
  accentable: true,
  variants: [
    { id: 'rect', name: 'Rectangular' },
    { id: 'round', name: 'Redonda' },
  ],
  keywords: [
    'mesa',
    'centro',
    'auxiliar',
    'lounge',
    'sala de espera',
    'coffee table',
    'side table',
  ],
  Component: TableCoffee,
}
